"use client";

import { c as _c } from "react/compiler-runtime";
import { Typography } from "@repo/ui/aops-components/typography";
import type { DiscoveryPageQueryResult } from "@repo/sanity-utils/schema/types";
import { Carousel } from "@repo/ui/mantine";
import styles from "./DiscoveryTabbedCarousel.module.css";
import layoutStyles from "../../layout.module.css";
import { ArrowButtons } from "./ArrowButtons/ArrowButtons";
import { CarouselContext } from "./CarouselContext";
import { useEffect, useMemo, useRef, useState, type ComponentProps, ReactNode } from "react";
import TabButton from "./TabButton/TabButton";
import { AopsPortableText } from "../../PortableText/PortableTextComponents";
import Image from "next/image";
import SlideBreadcrumbs from "./SlideBreadcrumbs/SlideBreadcrumbs";
import { PanelCornerDots } from "../../PanelCornerDots";
type DiscoveryPageBlocks = NonNullable<NonNullable<DiscoveryPageQueryResult>["pageContent"]>;
type DiscoveryTabbedCarouselBlock = Extract<DiscoveryPageBlocks[number], {
  _type: "discoveryTabbedCarouselBlock";
}>;
type Slide = NonNullable<DiscoveryTabbedCarouselBlock["slides"]>[number];
function slideTitle(slide: Slide): string {
  if (slide.titleSource === "modality") {
    return slide.modality?.title ?? "";
  }
  return slide.title ?? "";
}

/**
 * Detect when a statistic has an ordinal suffix and format it accordingly.
 * Solver Slab, which the "stat" variant uses, does not support ordinal formatting.
 * So, this finds "st", "nd", "rd", and "th" suffixes and formats them manually, with <sup> tags.
 *
 * If the string doesn't contain an ordinal suffix, it is returned as is.
 *
 * @example formatStatisticValue("1st") => 1<sup>st</sup>
 * @param value - The statistic value to format.
 * @returns The formatted statistic value.
 */
function formatStatisticValue(value: string): ReactNode {
  const regex = /(?<=\d)(st|nd|rd|th)/g;
  const parts = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(value)) !== null) {
    const [fullMatch] = match;
    const matchStart = match.index;
    const matchEnd = matchStart + fullMatch.length;
    if (lastIndex < matchStart) {
      parts.push(value.substring(lastIndex, matchStart));
    }
    parts.push(<sup key={matchStart}>{fullMatch}</sup>);
    lastIndex = matchEnd;
  }
  if (lastIndex < value.length) {
    parts.push(value.substring(lastIndex));
  }
  return parts.length > 0 ? parts : value;
}
type DiscoveryTabbedCarouselProps = {
  tabbedCarousel: DiscoveryTabbedCarouselBlock;
  slideImageUrls: {
    tab?: string;
    body?: string;
    testimonial?: string[];
  }[];
};
type EmblaApi = Parameters<NonNullable<ComponentProps<typeof Carousel>["getEmblaApi"]>>[0];
function allowTouchDrag(_api: unknown, event: MouseEvent | TouchEvent | PointerEvent) {
  if ("pointerType" in event) {
    return event.pointerType !== "mouse";
  }
  return event.type.startsWith("touch");
}
const EMBLA_OPTIONS = {
  loop: false,
  align: "start" as const,
  duration: 0,
  watchDrag: allowTouchDrag
};

/** Matches the two-column slide layout in DiscoveryTabbedCarousel.module.css */
const TWO_COLUMN_MQ = "(min-width: 1024px)";
function syncStackedViewportHeight(embla: EmblaApi) {
  const viewport = embla.containerNode().parentElement;
  if (!viewport) {
    return;
  }
  if (window.matchMedia(TWO_COLUMN_MQ).matches) {
    viewport.style.height = "";
    return;
  }
  const slide = embla.slideNodes()[embla.selectedScrollSnap()];
  if (!slide) {
    return;
  }
  viewport.style.height = `${slide.getBoundingClientRect().height}px`;
}

/** Keep the start of the stacked carousel on-screen after a height change. */
function revealStackedCarouselStart(container: HTMLElement | null) {
  if (!container || window.matchMedia(TWO_COLUMN_MQ).matches) {
    return;
  }
  const top = container.getBoundingClientRect().top;
  if (top >= 0) {
    return;
  }
  window.scrollBy({
    top
  });
}
function SlideTestimonialContent(t0) {
  const $ = _c(25);
  const {
    slide,
    testimonialImageUrls
  } = t0;
  let content = null;
  if (slide.testimonial?.detailType === "stats") {
    let t1;
    if ($[0] !== slide.testimonial?.stats) {
      t1 = slide.testimonial?.stats?.map(_temp);
      $[0] = slide.testimonial?.stats;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <div className={styles.slideTestimonialStats}>{t1}</div>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    content = t2;
  } else {
    if (slide.testimonial?.detailType === "images") {
      let t1;
      if ($[4] !== testimonialImageUrls) {
        t1 = testimonialImageUrls?.map(_temp2);
        $[4] = testimonialImageUrls;
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      let t2;
      if ($[6] !== t1) {
        t2 = <div className={styles.slideTestimonialImages}>{t1}</div>;
        $[6] = t1;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      content = t2;
    } else {
      if (slide.testimonial?.detailType === "textList") {
        let t1;
        if ($[8] !== slide.testimonial?.textList) {
          t1 = slide.testimonial?.textList?.map(_temp3);
          $[8] = slide.testimonial?.textList;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        let t2;
        if ($[10] !== t1) {
          t2 = <ul className={styles.slideTestimonialTextList}>{t1}</ul>;
          $[10] = t1;
          $[11] = t2;
        } else {
          t2 = $[11];
        }
        content = t2;
      }
    }
  }
  const t1 = `var(${slide.accentColor})`;
  let t2;
  if ($[12] !== t1) {
    t2 = {
      "--accent-color": t1
    };
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const t3 = t2 as React.CSSProperties;
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <PanelCornerDots className={styles.slideTestimonialDotsStart} dotClassName={styles.slideTestimonialDot} />;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== slide.testimonial?.source || $[16] !== slide.testimonial?.text) {
    t5 = slide.testimonial?.text && <div><Typography as="p" variant="bodyXl">{slide.testimonial?.text}</Typography><Typography as="p" variant="tag" className={styles.slideTestimonialSource}>{slide.testimonial?.source}</Typography></div>;
    $[15] = slide.testimonial?.source;
    $[16] = slide.testimonial?.text;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== content || $[19] !== t5) {
    t6 = <div className={styles.slideTestimonialContent}>{t5}{content}</div>;
    $[18] = content;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <PanelCornerDots className={styles.slideTestimonialDotsEnd} dotClassName={styles.slideTestimonialDot} />;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t6) {
    t8 = <div style={t3} className={styles.slideTestimonialCard}>{t4}{t6}{t7}</div>;
    $[22] = t3;
    $[23] = t6;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
function _temp3(text) {
  return <li key={text}><Typography as="span" variant="h5">{text}</Typography></li>;
}
function _temp2(url) {
  return <Image key={url} src={url} alt="" width={120} height={40} loading="eager" />;
}
function _temp(t0) {
  const {
    _key,
    value,
    subtitle,
    description
  } = t0;
  return <div key={_key} className={styles.slideTestimonialStat}><div className={styles.slideTestimonialStatValue}><Typography as="span" variant="stat">{formatStatisticValue(value)}</Typography>{subtitle && <Typography as="span" variant="body">{subtitle}</Typography>}</div><Typography as="span" variant="tag" className={styles.slideTestimonialStatDescription}>{description}</Typography></div>;
}
function SlideMedia(t0) {
  const $ = _c(6);
  const {
    slide,
    bodyImageUrl,
    testimonialImageUrls
  } = t0;
  if (slide.contentType === "image") {
    let t1;
    if ($[0] !== bodyImageUrl || $[1] !== slide) {
      t1 = bodyImageUrl ? <Image src={bodyImageUrl} alt={slideTitle(slide)} width={640} height={480} sizes="(max-width: 1024px) 100vw, 720px" quality={100} loading="eager" className={styles.slideImage} /> : <div className={styles.slideImage} />;
      $[0] = bodyImageUrl;
      $[1] = slide;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (slide.contentType === "text") {
    let t1;
    if ($[3] !== slide || $[4] !== testimonialImageUrls) {
      t1 = <SlideTestimonialContent slide={slide} testimonialImageUrls={testimonialImageUrls} />;
      $[3] = slide;
      $[4] = testimonialImageUrls;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  return null;
}
export function DiscoveryTabbedCarousel(t0) {
  const $ = _c(60);
  const {
    tabbedCarousel,
    slideImageUrls
  } = t0;
  const [embla, setEmbla] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselContainerRef = useRef(null);
  let t1;
  if ($[0] !== tabbedCarousel.slides) {
    t1 = tabbedCarousel.slides ?? [];
    $[0] = tabbedCarousel.slides;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const slides = t1;
  const slideCount = slides.length;
  const title = tabbedCarousel.title ?? "Carousel";
  let t2;
  let t3;
  let t4;
  if ($[2] !== embla) {
    t2 = index => {
      embla?.scrollTo(index);
    };
    t3 = () => {
      embla?.scrollPrev();
    };
    t4 = () => {
      embla?.scrollNext();
    };
    $[2] = embla;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  const t5 = activeIndex > 0;
  const t6 = activeIndex < slideCount - 1;
  let t7;
  if ($[6] !== activeIndex || $[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      activeIndex,
      setActiveIndex: t2,
      goToPrevious: t3,
      goToNext: t4,
      hasPrevious: t5,
      hasNext: t6
    };
    $[6] = activeIndex;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const carouselContext = t7;
  let t8;
  let t9;
  if ($[13] !== embla) {
    t8 = () => {
      if (!embla) {
        return;
      }
      const syncHeight = () => syncStackedViewportHeight(embla);
      const observer = new ResizeObserver(syncHeight);
      const observeActiveSlide = () => {
        observer.disconnect();
        const slide = embla.slideNodes()[embla.selectedScrollSnap()];
        if (slide) {
          observer.observe(slide);
        }
      };
      const syncActiveSlide = () => {
        observeActiveSlide();
        syncHeight();
      };
      let readyToReveal = false;
      const onSelect = () => {
        syncActiveSlide();
        if (!readyToReveal) {
          return;
        }
        revealStackedCarouselStart(carouselContainerRef.current);
      };
      embla.on("select", onSelect);
      embla.on("reInit", syncActiveSlide);
      window.addEventListener("resize", syncHeight);
      onSelect();
      readyToReveal = true;
      return () => {
        embla.off("select", onSelect);
        embla.off("reInit", syncActiveSlide);
        window.removeEventListener("resize", syncHeight);
        observer.disconnect();
      };
    };
    t9 = [embla];
    $[13] = embla;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  if ($[16] !== tabbedCarousel.eyebrowText) {
    t10 = tabbedCarousel.eyebrowText && <Typography as="p" variant="eyebrow" className={styles.eyebrow}>{tabbedCarousel.eyebrowText}</Typography>;
    $[16] = tabbedCarousel.eyebrowText;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== tabbedCarousel.title) {
    t11 = <Typography as="h2" variant="h2" className={styles.sectionTitleText}>{tabbedCarousel.title}</Typography>;
    $[18] = tabbedCarousel.title;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== tabbedCarousel.subtitle) {
    t12 = tabbedCarousel.subtitle && <Typography as="p" variant="bodyLarge" className={styles.subtitle}>{tabbedCarousel.subtitle}</Typography>;
    $[20] = tabbedCarousel.subtitle;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t12) {
    t13 = <div className={styles.sectionTitleTextRegion}>{t10}{t11}{t12}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className={styles.arrowButtonsRegion}><ArrowButtons /></div>;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t13) {
    t15 = <div className={styles.sectionTitleRegion}>{t13}{t14}</div>;
    $[27] = t13;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== slideImageUrls || $[30] !== slides) {
    let t17;
    if ($[32] !== slideImageUrls) {
      t17 = (slide_0, index_0) => <TabButton key={slide_0._key} index={index_0} text={slideTitle(slide_0)} iconUrl={slideImageUrls[index_0]?.tab ?? null} accentColor={slide_0.accentColor} />;
      $[32] = slideImageUrls;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    t16 = slides.map(t17);
    $[29] = slideImageUrls;
    $[30] = slides;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <div className={styles.tabButtons}>{t16}</div>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      root: styles.carouselRoot,
      viewport: styles.carouselViewport,
      container: styles.carouselTrack,
      slide: styles.carouselSlide
    };
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== slideImageUrls || $[38] !== slides) {
    let t20;
    if ($[40] !== slideImageUrls) {
      t20 = (slide_1, index_1) => <Carousel.Slide key={slide_1._key}><div className={styles.slideBody}><div className={styles.slideTitleBlock}><div className={styles.slideTitleRegion}><Typography as="h3" variant="h4" className={styles.slideTitleText}>{slideTitle(slide_1)}</Typography><AopsPortableText value={slide_1.description} /></div></div><SlideMedia slide={slide_1} bodyImageUrl={slideImageUrls[index_1]?.body} testimonialImageUrls={slideImageUrls[index_1]?.testimonial ?? []} /></div></Carousel.Slide>;
      $[40] = slideImageUrls;
      $[41] = t20;
    } else {
      t20 = $[41];
    }
    t19 = slides.map(t20);
    $[37] = slideImageUrls;
    $[38] = slides;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[42] !== t19 || $[43] !== title) {
    t20 = <Carousel classNames={t18} slideSize="100%" slideGap={0} withControls={false} withIndicators={false} emblaOptions={EMBLA_OPTIONS} getEmblaApi={setEmbla} onSlideChange={setActiveIndex} aria-label={title}>{t19}</Carousel>;
    $[42] = t19;
    $[43] = title;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== slides.length) {
    t21 = <div className={styles.pinnedBreadcrumbs}><SlideBreadcrumbs numSlides={slides.length} /></div>;
    $[45] = slides.length;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t20 || $[48] !== t21) {
    t22 = <div className={styles.slideStage}>{t20}{t21}</div>;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className={styles.mobileArrowButtonsRegion}><ArrowButtons /></div>;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t17 || $[52] !== t22) {
    t24 = <div ref={carouselContainerRef} className={styles.carouselContainer}>{t17}{t22}{t23}</div>;
    $[51] = t17;
    $[52] = t22;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t15 || $[55] !== t24) {
    t25 = <section className={styles.carouselSection}><div className={layoutStyles.content}>{t15}{t24}</div></section>;
    $[54] = t15;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== carouselContext || $[58] !== t25) {
    t26 = <CarouselContext.Provider value={carouselContext}>{t25}</CarouselContext.Provider>;
    $[57] = carouselContext;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  return t26;
}